import {getAddictionUnit, getRetentionBucketName} from 'common/report/retention/util';
import {formatMetricNumber, formatPercent, htmlEncodeString} from 'common/util';
import {TooltipItem, TooltipItemType} from 'common/components/chart-tooltip/types';
import {FormattedRow, RetentionBookmarkParams, RetentionType} from 'common/report/retention/util/types';
import {getFrequencyTooltipSubtitle, isAddiction} from 'common/report/retention/util';
import {AGGREGATED_ROW_NAME} from 'common/report/retention/util/constants';
import {getDataGroupName, isSegmented} from 'common/report/retention/util/format';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {THEME} from 'common/components/text-highlight';
import {formatLiftPercent} from 'common/report/util/lift';

import type {RetentionCurveChart} from './index';

// Axis label functions

export function retentionBucketLabel(bookmarkParams: RetentionBookmarkParams) {
  return function (value: number) {
    const {
      retention_type: retentionType,
      retention_unit: retentionUnit,
      date_unit: dateUnit,
      bucket_sizes: bucketSizes,
    } = bookmarkParams;

    return getRetentionBucketName({
      bucketIndex: value,
      retentionType,
      retentionUnit,
      dateUnit,
      bucketSizes,
    });
  };
}

export function frequencyBucketLabel(unit: string) {
  return function (value: number) {
    return `${value + 2} ${getAddictionUnit(unit)}s`;
  };
}

export function bucketLabeler(bookmarkParams: RetentionBookmarkParams) {
  return isAddiction(bookmarkParams)
    ? frequencyBucketLabel(bookmarkParams.date_unit)
    : retentionBucketLabel(bookmarkParams);
}

export function percentLabel(value: number) {
  return formatPercent(value, 0);
}

export function absoluteLabel(value: number) {
  return formatMetricNumber(value);
}

export function percentOrAbsoluteLabel(params: RetentionBookmarkParams) {
  if (params.lift_comparison) {
    return percentLabel;
  }
  return params.percentage_view ? percentLabel : absoluteLabel;
}

// Axis size functions
const MAX_X_TICKS = 20;
export function computeTicks(maxTicks = MAX_X_TICKS) {
  return function (min: number, max: number): number[] {
    const xTicks = [min];
    const skip = max - min + 1 > maxTicks ? Math.floor((max - min + 1) / maxTicks) : 0;
    while (min <= max) {
      if (min > xTicks[xTicks.length - 1] + skip) {
        xTicks.push(min);
      }
      min += 1;
    }
    return xTicks;
  };
}

// Chart description functions

// Tooltip generating functions
export function getLineChartTooltip(lineChart: RetentionCurveChart, bookmarkParams: RetentionBookmarkParams) {
  return function (x: number, _y: number, series: any, _idx: number) {
    const row = lineChart.selectedSegmentsRows.find((row) => row.raw_head === series.userOptions.id);
    const liftData = lineChart.liftDataForSegment(row);
    const cell = row.data[x];

    const dataGroupName = getDataGroupName(bookmarkParams.data_group_id);
    const isAggregated = isAggregateRow(row, bookmarkParams);
    const {titleSection, subtitle} = getTitleAndSubtitle(bookmarkParams, row, x);

    const retentionAddictionLabel = {
      text: isAddiction(bookmarkParams) ? `engaged` : `retention`,
      type: TooltipItemType.Text,
    };

    let bodySection;
    if (liftData) {
      const liftCell = liftData.liftRow.data[x];
      const baselineRow = liftData.baselineRow;
      const baselineCell = baselineRow?.data[x];

      let liftDisplayValue, currentSegmentReferenceValue, baselineSegmentReferenceValue;
      let isNegative = false;
      if (bookmarkParams.percentage_view) {
        liftDisplayValue = liftCell.formatted_percent;
        isNegative = liftCell.percent < 0;
        currentSegmentReferenceValue = cell.formatted_percent;
        baselineSegmentReferenceValue = baselineCell.formatted_percent;
      } else {
        liftDisplayValue = formatLiftPercent(liftCell.value);
        isNegative = liftCell.value < 0;
        currentSegmentReferenceValue = cell.formatted_value;
        baselineSegmentReferenceValue = baselineCell.formatted_value;
      }

      bodySection = {
        rows: [
          {
            items: [
              {
                text: liftDisplayValue,
                type: TooltipItemType.Data,
                theme: isNegative ? THEME.NEGATIVE : THEME.POSITIVE,
              },
            ],
          },
          {
            items: [
              {text: currentSegmentReferenceValue, type: TooltipItemType.Data},
              ...(bookmarkParams.percentage_view ? [retentionAddictionLabel] : [{}]),
              {text: row.display_head, type: TooltipItemType.Text},
            ],
          },
          ...(baselineRow && baselineRow.raw_head !== row.raw_head
            ? [
                {
                  items: [
                    {text: baselineSegmentReferenceValue, type: TooltipItemType.Data},
                    ...(bookmarkParams.percentage_view ? [retentionAddictionLabel] : [{}]),
                    {text: baselineRow.display_head, type: TooltipItemType.Text},
                  ],
                },
              ]
            : []),
        ],
      };
    } else {
      bodySection = {
        rows: [
          {
            items: [{text: cell.formatted_percent, type: TooltipItemType.Data}, retentionAddictionLabel],
          },
          {
            items: [
              {text: cell.formatted_value, type: TooltipItemType.Data},
              {
                text: isAggregated ? `${dataGroupName}s (average)` : `${dataGroupName}s`,
                type: TooltipItemType.Text,
              },
            ],
          },
        ],
      };
    }
    const sections = [
      {
        rows: [
          {
            items: titleSection,
          },
          {
            items: [{text: subtitle, type: TooltipItemType.Detail}],
          },
        ],
      },
      bodySection,
    ];

    const footerText = cell.incomplete ? `footer-text="Incomplete ${bookmarkParams.date_unit}" ` : ``;

    return `<mp-chart-tooltip title-color=${row.selection_color} ${footerText} sections="${htmlEncodeString(
      JSON.stringify(sections),
    )}">`;
  };
}

function getTitleAndSubtitle(bookmarkParams: RetentionBookmarkParams, row: FormattedRow, x: number) {
  const cell = row.data[x];
  const isAggregatedRow = isAggregateRow(row, bookmarkParams);

  const title = isAddiction(bookmarkParams)
    ? getTitle(isSegmented(bookmarkParams.group_by), isAggregatedRow, bookmarkParams.retention_type)
    : getRetentionBucketName({
        bucketIndex: x,
        retentionType: bookmarkParams.retention_type,
        retentionUnit: bookmarkParams.retention_unit,
        dateUnit: bookmarkParams.date_unit,
        bucketSizes: bookmarkParams.bucket_sizes,
      });

  const subtitle = isAddiction(bookmarkParams)
    ? getFrequencyTooltipSubtitle({
        cumulative: bookmarkParams.cumulative,
        dateUnit: bookmarkParams.date_unit as TimeUnit,
        bucketIndex: x,
      })
    : retentionTooltipSubtitle(
        isSegmented(bookmarkParams.group_by),
        isAggregatedRow,
        bookmarkParams.retention_type,
        cell.incomplete,
      );

  const titleSection: TooltipItem | TooltipItem[] = [{text: title, type: TooltipItemType.Strong}];

  return {subtitle, titleSection};

  function getTitle(segmented: boolean, isAggregated: boolean, retentionType: RetentionType) {
    if (segmented) {
      return row.raw_head;
    } else if (isAggregated) {
      return AGGREGATED_ROW_NAME(retentionType);
    } else {
      return `Users entered on ${cell.entered_value}`;
    }
  }

  function retentionTooltipSubtitle(
    segmented: boolean,
    isAggregated: boolean,
    retentionType: RetentionType,
    incomplete: boolean,
  ) {
    const isAggregatedAndComplete = isAggregated && !incomplete;

    return (
      getTitle(segmented, isAggregated, retentionType) + (isAggregatedAndComplete ? ` in completed intervals` : ``)
    );
  }
}

function isAggregateRow(row: FormattedRow, bookmarkParams: RetentionBookmarkParams) {
  return (
    isSegmented(bookmarkParams.group_by) || row.display_head === AGGREGATED_ROW_NAME(bookmarkParams.retention_type)
  );
}
